import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { ContentLanguageCode, FIRESTORE_COLLECTIONS } from '@/shared/constants';
import { getFirebaseDb } from '@/shared/services/firebase/config';
import { MuseumPlan, Work } from '@/shared/types';
import { uploadAudioToStorage, uploadImageToStorage, getFirebaseImageUrl } from '@/shared/utils/imageUtils';
import { logger } from '@/shared/utils/logger';
import { validateWork } from '@/shared/utils/validation';
import { sanitizeMultilingualHtml } from '@/shared/utils/sanitizeHtml';
import { convertLocationToMultilingual } from './workLocationUtils';
import { getAudioDurationFromFile, getAudioDurationFromUrl } from './audioDuration';
import type { AudioDescriptionInput } from './audioDescription';
import { buildImageAlt } from './imageAlt';

export type SaveWorkResult =
  | { ok: true }
  | { ok: false; code: 'database' }
  | { ok: false; code: 'order' }
  | { ok: false; code: 'validation'; errors: string[] };

export interface SaveWorkParams {
  museumId: string;
  exhibitionId: string;
  work?: Work | null;
  isEditMode: boolean;
  order: number;
  allWorks: Work[];
  coverImage: File | null;
  /** Vignette supprimée dans la modale (sans nouvelle image). */
  coverRemoved?: boolean;
  /** Description de la vignette par langue. */
  imageAlt?: Record<ContentLanguageCode, string>;
  audioFile: Record<ContentLanguageCode, File | null>;
  activeLanguages: ContentLanguageCode[];
  title: Record<ContentLanguageCode, string>;
  transcription: Record<ContentLanguageCode, string>;
  statusByLanguage: Record<string, 'draft' | 'published'>;
  floor: Record<ContentLanguageCode, string>;
  room: Record<ContentLanguageCode, string>;
  pillNumber: string;
  audioDescription?: AudioDescriptionInput;
  museumPlans?: MuseumPlan[];
  changeWorkOrder: (items: Work[], itemId: string, newOrder: number) => Promise<boolean>;
  loadAllWorks: () => Promise<Work[] | void>;
}

const toDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) {
    return value;
  }
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return undefined;
};

/**
 * Audiodescription par langue : envoie les nouveaux fichiers, mesure leur durée,
 * retire les audios supprimés et assainit la transcription. Les langues non
 * actives gardent leurs valeurs.
 */
async function saveAudioDescription({
  museumId,
  exhibitionId,
  work,
  activeLanguages,
  audioDescription,
}: {
  museumId: string;
  exhibitionId: string;
  work?: Work | null;
  activeLanguages: ContentLanguageCode[];
  audioDescription: AudioDescriptionInput;
}) {
  const urls: Record<string, string> = { ...(work?.audioDescriptionUrl || {}) };
  const durations: Record<string, number> = { ...(work?.audioDescriptionDurationTimeInSeconds || {}) };
  const transcriptions: Record<string, string> = { ...(work?.audioDescriptionTranscription || {}) };

  for (const lang of activeLanguages) {
    const file = audioDescription.files[lang];
    if (file) {
      const extension = file.name.split('.').pop() || 'mp3';
      const path = `museums/${museumId}/exhibitions/${exhibitionId}/works/audio_description_${lang}_${Date.now()}.${extension}`;
      urls[lang] = await uploadAudioToStorage(file, path);
      try {
        durations[lang] = await getAudioDurationFromFile(file);
      } catch (error) {
        logger.error(`Erreur lors du calcul de la durée de l'audiodescription pour ${lang}:`, error);
        delete durations[lang];
      }
    } else if (!audioDescription.kept[lang]) {
      delete urls[lang];
      delete durations[lang];
    }
    transcriptions[lang] = audioDescription.transcription[lang] || '';
  }

  return {
    audioDescriptionUrl: urls,
    audioDescriptionDurationTimeInSeconds: durations,
    audioDescriptionTranscription: sanitizeMultilingualHtml(transcriptions),
  };
}

export const saveWork = async ({
  museumId,
  exhibitionId,
  work,
  isEditMode,
  order,
  allWorks,
  coverImage,
  coverRemoved = false,
  imageAlt = {} as Record<ContentLanguageCode, string>,
  audioFile,
  activeLanguages,
  title,
  transcription,
  statusByLanguage,
  floor,
  room,
  pillNumber,
  audioDescription,
  museumPlans,
  changeWorkOrder,
  loadAllWorks,
}: SaveWorkParams): Promise<SaveWorkResult> => {
  const db = getFirebaseDb();
  if (!db) {
    return { ok: false, code: 'database' };
  }

  if (isEditMode && work && work.order !== order && allWorks.length > 0) {
    try {
      await changeWorkOrder(allWorks, work.id, order);
      await loadAllWorks();
    } catch (error) {
      logger.error('Erreur lors de la réorganisation des œuvres:', error);
      return { ok: false, code: 'order' };
    }
  }

  let imageUrl = coverRemoved ? '' : work?.imageUrl || '';
  if (coverImage) {
    const timestamp = Date.now();
    const fileExtension = coverImage.name.split('.').pop() || 'jpg';
    const imagePath = `museums/${museumId}/exhibitions/${exhibitionId}/works/cover_${timestamp}.${fileExtension}`;
    imageUrl = await uploadImageToStorage(coverImage, imagePath);
  }

  const audioUrl: Record<string, string> = { ...(work?.audioUrl || {}) };
  const durationTimeInSecondsByLang: Record<string, number> = {};

  if (work?.durationTimeInSeconds) {
    Object.assign(durationTimeInSecondsByLang, work.durationTimeInSeconds);
  }

  if (audioFile) {
    for (const lang of activeLanguages) {
      const audioFileForLang = audioFile[lang];
      if (audioFileForLang) {
        const timestamp = Date.now();
        const fileExtension = audioFileForLang.name.split('.').pop() || 'mp3';
        const audioPath = `museums/${museumId}/exhibitions/${exhibitionId}/works/audio_${lang}_${timestamp}.${fileExtension}`;
        audioUrl[lang] = await uploadAudioToStorage(audioFileForLang, audioPath);

        try {
          const duration = await getAudioDurationFromFile(audioFileForLang);
          durationTimeInSecondsByLang[lang] = duration;
        } catch (error) {
          logger.error(`Erreur lors du calcul de la durée de l'audio pour ${lang}:`, error);
        }
      }
    }
  }

  for (const lang of activeLanguages) {
    if (!durationTimeInSecondsByLang[lang] && audioUrl[lang]) {
      try {
        const audioUrlForLang = await getFirebaseImageUrl(audioUrl[lang]);
        const duration = await getAudioDurationFromUrl(audioUrlForLang);
        durationTimeInSecondsByLang[lang] = duration;
      } catch (error) {
        logger.error(`Erreur lors du calcul de la durée de l'audio existant pour ${lang}:`, error);
      }
    }
  }

  const audioDescriptionData = audioDescription
    ? await saveAudioDescription({ museumId, exhibitionId, work, activeLanguages, audioDescription })
    : {};

  const nameTranslations: Record<string, string> = {};
  const descriptionTranslations: Record<string, string> = {};
  const statusByLang: Record<string, 'draft' | 'published'> = {};

  activeLanguages.forEach((lang: ContentLanguageCode) => {
    nameTranslations[lang] = title[lang] || '';
    descriptionTranslations[lang] = transcription[lang] || '';
    statusByLang[lang] = statusByLanguage[lang] || 'draft';
  });

  const sanitizedDescriptions = sanitizeMultilingualHtml(descriptionTranslations);
  Object.assign(descriptionTranslations, sanitizedDescriptions);

  const validation = validateWork({
    name: nameTranslations,
    description: descriptionTranslations,
    audioUrl,
    status: statusByLang,
    order,
  });

  if (!validation.isValid) {
    return { ok: false, code: 'validation', errors: validation.errors };
  }

  const now = new Date();
  const createdAtByLang: Record<string, Date> = {};
  const updatedAtByLang: Record<string, Date> = {};

  if (isEditMode && work?.createdAt) {
    Object.keys(work.createdAt).forEach((lang) => {
      const existingDate = toDate(work.createdAt[lang]);
      if (existingDate) {
        createdAtByLang[lang] = existingDate;
      }
    });
  } else {
    activeLanguages.forEach((lang) => {
      createdAtByLang[lang] = now;
    });
  }

  activeLanguages.forEach((lang) => {
    updatedAtByLang[lang] = now;
  });

  const enrichedFloor = convertLocationToMultilingual(floor, activeLanguages, 'floor', museumPlans);
  const enrichedRoom = convertLocationToMultilingual(room, activeLanguages, 'room', museumPlans, enrichedFloor);

  const workData = {
    name: nameTranslations,
    description: descriptionTranslations,
    audioUrl,
    imageUrl: imageUrl || null,
    imageAlt: buildImageAlt({ existing: work?.imageAlt, imageAlt, activeLanguages, hasImage: Boolean(imageUrl) }),
    status: statusByLang,
    floor: enrichedFloor,
    room: enrichedRoom,
    pillNumber:
      pillNumber === '-' || pillNumber === ''
        ? null
        : typeof pillNumber === 'string'
          ? parseInt(pillNumber, 10) || null
          : pillNumber,
    order,
    durationTimeInSeconds: durationTimeInSecondsByLang,
    ...audioDescriptionData,
    createdAt: createdAtByLang,
    updatedAt: updatedAtByLang,
  };

  if (isEditMode && work) {
    const workRef = doc(
      db,
      FIRESTORE_COLLECTIONS.MUSEUMS,
      museumId,
      FIRESTORE_COLLECTIONS.EXHIBITIONS,
      exhibitionId,
      FIRESTORE_COLLECTIONS.WORKS,
      work.id
    );
    await updateDoc(workRef, workData);
  } else {
    const worksRef = collection(
      db,
      FIRESTORE_COLLECTIONS.MUSEUMS,
      museumId,
      FIRESTORE_COLLECTIONS.EXHIBITIONS,
      exhibitionId,
      FIRESTORE_COLLECTIONS.WORKS
    );
    await addDoc(worksRef, {
      ...workData,
      museumId,
      exhibitionId,
    });
  }

  return { ok: true };
};
